import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";

// A Training Curriculum's own content: what it trains people on, who owes the training, how often it
// repeats. The record's identity, revisions, lifecycle and approvals are the ordinary object endpoints.
//
// THE PERSONA TRAVELS ON EVERY REQUEST via the axios instance, which matters here beyond access: the
// positions offered as trainees come from the organization that owns the ACTIVE position.

export type FrequencyUnit = "DAYS" | "WEEKS" | "MONTHS" | "YEARS";
export type AudienceType = "Position" | "UserGroup" | "WorkGroup" | "AccountUser";

export type CurriculumItem = {
    USID?: string;
    ObjectUSID: string;
    ObjectID?: string | null;
    Name?: string | null;
    ObjectClassification?: string | null;
    DisplayRevision?: string | null;
    Status?: string | null;
    /** Said out loud, because training can be switched off on a record a plan still names. */
    TrainingRequired?: boolean;
    TrainingRequireQuiz?: boolean;
    CurrentRevisionUSID?: string | null;
    /** The position that owns the record, so a row naming one the reader cannot open explains itself. */
    OwnerPositionName?: string | null;
};

export type AudienceMember = {
    USID?: string;
    AudienceType: AudienceType;
    AudienceUSID: string;
    Name?: string | null;
};

/** One trainee named individually who cannot reach one or more of the named records.
 *  Carries the USID as well as the name: a warning identified only by name cannot be reconciled
 *  against the picker, so a person removed from the box went on being named by it. */
export type BlindTrainee = {
    AudienceUSID?: string;
    Name: string | null;
    Records: (string | null)[];
};

export type AudienceCheck = {
    BlindTrainees: BlindTrainee[];
    TraineesWithoutAccess: number;
    TraineesCovered: number;
};

export type Curriculum = {
    USID: string;
    RefreshPeriodically: boolean;
    FrequencyCount: number | null;
    FrequencyUnit: FrequencyUnit | null;
    /** Days a trainee gets before this is overdue. null = no due date, and nothing goes red. */
    GracePeriodDays: number | null;
    /** Trainees named as INDIVIDUALS who genuinely cannot reach a named record under any position
     *  they hold. Records are shared with a position or a group, never a person, so releasing this
     *  will not help them. Empty when everyone named by name can already see everything. */
    BlindTrainees?: BlindTrainee[];
    TraineesWithoutAccess?: number;
    /** Trainees named as a position or group, who will be able to read every item once released. */
    TraineesCovered?: number;
    Items: CurriculumItem[];
    Audience: AudienceMember[];
};

export type CurriculumState = {
    ObjectRevisionUSID: string;
    Status: string;
    Editable: boolean;
    Curriculum: Curriculum | null;
};

export type TrainableObject = {
    ObjectUSID: string;
    ObjectID: string | null;
    Name: string | null;
    ObjectClassification: string | null;
    DisplayRevision: string | null;
    Status: string | null;
    TrainingRequireQuiz: boolean;
    /** The position that owns it. Shown because this picker is not persona-scoped, so it can
     *  offer a record the author cannot open anywhere else in the app. */
    OwnerPositionName: string | null;
};

export type AudienceCandidates = {
    Positions: {USID: string; Name: string; Organization?: string | null}[];
    /** Why the position list is empty, when it is. Null when it is populated. */
    PositionsUnavailableReason: string | null;
    UserGroups: {USID: string; Name: string}[];
    WorkGroups: {USID: string; Name: string}[];
    AccountUsers: {USID: string; Name: string; Email?: string | null}[];
};

export async function getCurriculum(sessionContext: any, objectRevisionUSID: string): Promise<CurriculumState> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.get("t1/v1/training_curricula", {params: {ObjectRevisionUSID: objectRevisionUSID}});
    return response.data;
}

/**
 * Items and Audience are replaced WHOLESALE when passed, and left alone when omitted -- so a save that
 * carries only the interval cannot empty the trainees. Omit them rather than passing an empty array,
 * which means "remove everything".
 */
export async function saveCurriculum(sessionContext: any, objectRevisionUSID: string, changes: {
    RefreshPeriodically?: boolean;
    FrequencyCount?: number | null;
    FrequencyUnit?: FrequencyUnit | null;
    /** Omit to leave the stored value alone; send null to clear the due date. */
    GracePeriodDays?: number | null;
    Items?: {ObjectUSID: string}[];
    Audience?: {AudienceType: AudienceType; AudienceUSID: string}[];
}): Promise<{Curriculum: Curriculum | null}> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.put("t1/v1/training_curricula",
                                   {ObjectRevisionUSID: objectRevisionUSID, ...changes});
    return response.data;
}

export async function getTrainableObjects(sessionContext: any, search?: string): Promise<TrainableObject[]> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.get("t1/v1/trainable_objects", {params: {Search: search || undefined}});
    return response.data?.Objects ?? [];
}

export async function getAudienceCandidates(sessionContext: any): Promise<AudienceCandidates> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.get("t1/v1/training_audience_candidates");
    return response.data;
}

/**
 * Ask whether the people currently in the picker can reach the records currently in the picker.
 *
 * THE UNSAVED LISTS, which is the whole reason this exists. The same check runs inside
 * getCurriculum, but that one can only answer for the curriculum as last SAVED -- so the warning
 * named people after they were removed and said nothing about whoever replaced them.
 */
export async function checkAudience(sessionContext: any, items: {ObjectUSID: string}[],
                                    audience: {AudienceType: AudienceType; AudienceUSID: string}[]
                                   ): Promise<AudienceCheck> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const response = await api.post("t1/v1/training_audience_check", {
        Items: items.map(one => ({ObjectUSID: one.ObjectUSID})),
        Audience: audience.map(one => ({AudienceType: one.AudienceType, AudienceUSID: one.AudienceUSID})),
    });
    return response.data;
}
